'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getReviewerToken,
  setReviewerToken,
  clearReviewerToken,
  fetchReviewerProfile,
  beginReviewerOAuth,
  getPendingReview,
  clearPendingReview,
  getPendingPhotos,
  clearPendingPhotos,
  submitReview,
  type ReviewerAuthProvider as AuthProvider,
} from '../utils/reviewerApi';
import type { ReviewerProfile } from '../types';

// ── Types ────────────────────────────────────────────────────

interface PendingReviewData {
  entityId: number;
  entityName: string;
  formData: Record<string, unknown>;
}

interface PendingPhotosData {
  entityId: number;
  captions: string[];
}

interface ReviewerAuthContextValue {
  /** The currently authenticated reviewer, or null */
  reviewer: ReviewerProfile | null;
  /** Whether the initial auth check is still running */
  isLoading: boolean;
  /** Shorthand for !!reviewer */
  isLoggedIn: boolean;
  /** Start OAuth flow for a provider */
  login: (provider: AuthProvider) => Promise<void>;
  /** Clear token and reset state */
  logout: () => void;
  /** Re-fetch the reviewer profile (e.g. after updating) */
  refreshProfile: () => Promise<void>;
  /** If a review was saved before OAuth redirect, it lives here */
  pendingReview: PendingReviewData | null;
  /** If community photos were queued before OAuth redirect */
  pendingPhotos: PendingPhotosData | null;
  /** Clear the pending review (after successful submission) */
  clearPending: () => void;
  /** Clear the pending photos (after successful upload) */
  clearPendingPhotosData: () => void;
  /** Auto-submit any pending review after OAuth callback */
  processPendingReview: () => Promise<boolean>;
}

const ReviewerAuthContext = createContext<ReviewerAuthContextValue | undefined>(
  undefined,
);

// ── Provider ─────────────────────────────────────────────────

export function ReviewerAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [reviewer, setReviewer] = useState<ReviewerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingReview, setPendingReview] = useState<PendingReviewData | null>(
    null,
  );
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhotosData | null>(
    null,
  );

  // ── Check for token in URL (OAuth callback) and validate ──

  const validateToken = useCallback(async () => {
    setIsLoading(true);
    try {
      // 1. Check if the OAuth callback just returned a token in the URL
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('reviewer_token');
        if (tokenFromUrl) {
          setReviewerToken(tokenFromUrl);
          // Clean the token from the URL to avoid leaking it in referrer etc.
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('reviewer_token');
          window.history.replaceState({}, '', cleanUrl.toString());
        }
      }

      // 2. If we have a token (either from URL or localStorage), validate it
      const token = getReviewerToken();
      if (!token) {
        setReviewer(null);
        return;
      }

      const profile = await fetchReviewerProfile();
      if (profile && profile.reviewer) {
        setReviewer(profile.reviewer);
      } else if (profile && profile.id) {
        // Some backends return the reviewer directly
        setReviewer(profile as unknown as ReviewerProfile);
      } else {
        // Token is invalid or expired
        clearReviewerToken();
        setReviewer(null);
      }
    } catch (err) {
      console.error('[ReviewerAuth] Token validation failed:', err);
      clearReviewerToken();
      setReviewer(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Load pending submissions from sessionStorage ──────────

  const loadPending = useCallback(() => {
    const review = getPendingReview();
    const photos = getPendingPhotos();
    if (review) setPendingReview(review);
    if (photos) setPendingPhotos(photos);
  }, []);

  // ── On mount: validate + load pending ─────────────────────

  useEffect(() => {
    validateToken();
    loadPending();
  }, [validateToken, loadPending]);

  // ── Auto-submit pending review after successful login ─────

  const processPendingReview = useCallback(async (): Promise<boolean> => {
    const pending = getPendingReview();
    if (!pending || !reviewer) return false;

    try {
      await submitReview(pending.entityId, pending.formData);
      clearPendingReview();
      setPendingReview(null);
      return true;
    } catch (err) {
      console.error('[ReviewerAuth] Auto-submit pending review failed:', err);
      return false;
    }
  }, [reviewer]);

  // After login completes and we have a reviewer, try to submit pending
  useEffect(() => {
    if (reviewer && pendingReview) {
      // Small delay to let UI settle
      const timer = setTimeout(() => {
        processPendingReview();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [reviewer, pendingReview, processPendingReview]);

  // ── Public API ────────────────────────────────────────────

  const login = useCallback(async (provider: AuthProvider) => {
    await beginReviewerOAuth(provider);
  }, []);

  const logout = useCallback(() => {
    clearReviewerToken();
    setReviewer(null);
    setPendingReview(null);
    setPendingPhotos(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    const token = getReviewerToken();
    if (!token) return;
    try {
      const profile = await fetchReviewerProfile();
      if (profile && profile.reviewer) {
        setReviewer(profile.reviewer);
      } else if (profile && profile.id) {
        setReviewer(profile as unknown as ReviewerProfile);
      }
    } catch (err) {
      console.error('[ReviewerAuth] Refresh profile failed:', err);
    }
  }, []);

  const clearPending = useCallback(() => {
    clearPendingReview();
    setPendingReview(null);
  }, []);

  const clearPendingPhotosData = useCallback(() => {
    clearPendingPhotos();
    setPendingPhotos(null);
  }, []);

  // ── Memoised context value ────────────────────────────────

  const value = useMemo<ReviewerAuthContextValue>(
    () => ({
      reviewer,
      isLoading,
      isLoggedIn: !!reviewer,
      login,
      logout,
      refreshProfile,
      pendingReview,
      pendingPhotos,
      clearPending,
      clearPendingPhotosData,
      processPendingReview,
    }),
    [
      reviewer,
      isLoading,
      login,
      logout,
      refreshProfile,
      pendingReview,
      pendingPhotos,
      clearPending,
      clearPendingPhotosData,
      processPendingReview,
    ],
  );

  return (
    <ReviewerAuthContext.Provider value={value}>
      {children}
    </ReviewerAuthContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────

export function useReviewerAuth(): ReviewerAuthContextValue {
  const ctx = useContext(ReviewerAuthContext);
  if (!ctx) {
    throw new Error(
      'useReviewerAuth must be used within a <ReviewerAuthProvider>',
    );
  }
  return ctx;
}

export default ReviewerAuthContext;
